import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Lock, LogOut, RefreshCw, Mail, HeartHandshake, PenLine, ArrowLeft, Flame } from 'lucide-react';
import { Button } from '../components/ui/button';
import { api } from '../lib/api';

const STORAGE_KEY = '40sam_admin_session';
const SESSION_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

function readSession() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.pw || !parsed.exp) return null;
    if (Date.now() > parsed.exp) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed.pw;
  } catch (_e) {
    return null;
  }
}

function writeSession(pw) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ pw, exp: Date.now() + SESSION_TTL_MS }));
  } catch (_e) {
    // localStorage may be unavailable (private mode, quota); auth still works in memory for this session.
  }
}

function clearSession() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (_e) {
    // ignore
  }
}

const KIND_META = {
  prayer:        { label: 'Prayer Requests', icon: HeartHandshake, color: 'text-[#D4AF37]' },
  chapter_lead:  { label: 'Chapter Leads',   icon: Mail,           color: 'text-[#3D5A80]' },
  exercise:      { label: 'Exercise Writing', icon: PenLine,       color: 'text-[#E63946]' },
};

const LoginScreen = ({ onLogin }) => {
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr('');
    try {
      await api.adminVerify(password);
      writeSession(password);
      onLogin(password);
    } catch (e2) {
      setErr('Incorrect password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-4">
      <form onSubmit={submit} className="w-full max-w-md bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border border-[#D4AF37]/30 rounded-3xl p-8" data-testid="admin-login-form">
        <div className="text-center mb-6">
          <div className="w-14 h-14 mx-auto rounded-full bg-gradient-to-br from-[#D4AF37] to-[#FFD700] flex items-center justify-center mb-4">
            <Lock className="w-7 h-7 text-[#0a0a0a]" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-1" style={{ fontFamily: '"Bebas Neue", sans-serif' }}>
            ADMIN
          </h1>
          <p className="text-gray-400 text-sm">40 Songs and a Mic</p>
        </div>
        <label className="block text-xs font-bold text-gray-400 mb-2 tracking-wider">PASSWORD</label>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          data-testid="admin-password-input"
          required
          autoFocus
          className="w-full bg-[#0a0a0a] border border-[#D4AF37]/30 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/40 outline-none rounded-lg px-4 py-3 text-white"
        />
        {err && <p className="text-[#E63946] text-sm mt-2" data-testid="admin-login-error">{err}</p>}
        <Button
          type="submit"
          disabled={loading || !password}
          data-testid="admin-login-submit"
          className="w-full mt-6 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0a0a0a] hover:from-[#FFD700] hover:to-[#D4AF37] font-bold py-6"
        >
          {loading ? 'CHECKING...' : 'ENTER'}
        </Button>
        <Link to="/" className="block text-center text-xs text-gray-500 hover:text-[#D4AF37] mt-4">
          Back to site
        </Link>
      </form>
    </div>
  );
};

const SubmissionCard = ({ sub }) => {
  const meta = KIND_META[sub.kind] || { label: sub.kind, icon: Mail, color: 'text-white' };
  const Icon = meta.icon;
  const when = new Date(sub.created_at).toLocaleString();
  const data = sub.data || {};

  return (
    <div className="bg-[#1a1a1a] border border-[#D4AF37]/10 hover:border-[#D4AF37]/30 rounded-xl p-5 transition-colors" data-testid={`submission-${sub.id}`}>
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${meta.color}`} />
          <span className="text-xs font-bold uppercase tracking-wider text-gray-400">{meta.label}</span>
          {data.urgent && (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#E63946] bg-[#E63946]/10 border border-[#E63946]/30 rounded-full px-2 py-0.5">
              <Flame className="w-3 h-3" /> URGENT
            </span>
          )}
        </div>
        <span className="text-xs text-gray-500">{when}</span>
      </div>

      {(data.name || data.email) && (
        <p className="text-sm text-gray-300 mb-2">
          {data.name && <span className="font-bold">{data.name}</span>}
          {data.name && data.email && ' · '}
          {data.email && <a href={`mailto:${data.email}`} className="text-[#D4AF37] hover:underline">{data.email}</a>}
        </p>
      )}

      {data.message && <p className="text-white whitespace-pre-wrap leading-relaxed">{data.message}</p>}
      {data.response && (
        <>
          {data.prompt && <p className="text-xs text-gray-500 italic mb-2">Prompt: {data.prompt.slice(0, 120)}{data.prompt.length > 120 ? '...' : ''}</p>}
          <p className="text-white whitespace-pre-wrap leading-relaxed font-serif">{data.response}</p>
        </>
      )}
      {!data.message && !data.response && data.email && <p className="text-gray-400 text-sm">Requested the sample chapter.</p>}
    </div>
  );
};

const AdminDashboard = () => {
  const [password, setPassword] = useState(() => readSession() || '');
  const [authed, setAuthed] = useState(false);
  const [counts, setCounts] = useState({});
  const [subs, setSubs] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(false);

  const loadData = async (pw) => {
    setLoading(true);
    try {
      const [countsData, subsData] = await Promise.all([
        api.adminCounts(pw),
        api.adminSubmissions(pw),
      ]);
      setCounts(countsData);
      setSubs(subsData.submissions || []);
    } catch (_e) {
      // Session expired -> log out
      clearSession();
      setPassword('');
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!password) return;
    api.adminVerify(password)
      .then(() => {
        setAuthed(true);
        return loadData(password);
      })
      .catch(() => {
        clearSession();
        setPassword('');
        setAuthed(false);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogin = (pw) => {
    setPassword(pw);
    setAuthed(true);
    loadData(pw);
  };

  const logout = () => {
    clearSession();
    setPassword('');
    setAuthed(false);
  };

  if (!authed) return <LoginScreen onLogin={handleLogin} />;

  const filtered = filter === 'all' ? subs : subs.filter((s) => s.kind === filter);
  const totalCount = Object.values(counts).reduce((sum, n) => sum + n, 0);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <header className="sticky top-0 z-40 bg-[#0a0a0a]/90 backdrop-blur-lg border-b border-[#D4AF37]/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2 text-gray-400 hover:text-[#D4AF37]" data-testid="admin-back-home">
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Back to site</span>
            </Link>
            <span className="text-gray-600">|</span>
            <span className="font-bold text-white" style={{ fontFamily: '"Bebas Neue", sans-serif' }}>ADMIN DASHBOARD</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => loadData(password)}
              variant="outline"
              size="sm"
              data-testid="admin-refresh"
              className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              onClick={logout}
              variant="outline"
              size="sm"
              data-testid="admin-logout"
              className="border-[#E63946]/40 text-[#E63946] hover:bg-[#E63946]/10"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <StatCard label="Total" value={totalCount} onClick={() => setFilter('all')} active={filter === 'all'} testid="stat-all" />
          {Object.entries(KIND_META).map(([kind, meta]) => (
            <StatCard
              key={kind}
              label={meta.label}
              value={counts[kind] || 0}
              onClick={() => setFilter(kind)}
              active={filter === kind}
              testid={`stat-${kind}`}
            />
          ))}
        </div>

        {/* Submissions list */}
        <div className="space-y-4" data-testid="admin-submissions-list">
          {filtered.length === 0 ? (
            <p className="text-center text-gray-500 py-16">No submissions yet.</p>
          ) : (
            filtered.map((s) => <SubmissionCard key={s.id} sub={s} />)
          )}
        </div>
      </main>
    </div>
  );
};

const StatCard = ({ label, value, onClick, active, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`text-left bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border rounded-2xl p-5 transition-all ${
      active ? 'border-[#D4AF37]' : 'border-[#D4AF37]/10 hover:border-[#D4AF37]/40'
    }`}
  >
    <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">{label}</p>
    <p className="text-3xl font-bold text-white" style={{ fontFamily: '"Bebas Neue", sans-serif' }}>{value}</p>
  </button>
);

export default AdminDashboard;
